import React, { useState, useEffect } from 'react';
import ReduxProvider from './store/ReduxProvider';
import { ProfileProvider } from './contexts/ProfileContext';
import { EmployeeProvider } from './contexts/EmployeeContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header/Header';
import Footer from './components/Footer';
import DocumentTitle from './components/DocumentTitle';
import UBTLandingPage from './pages/UBTAdmin/LandingPage/UBTLandingPage';
import AuthCallback from './components/common/Auth/CallBack';
import Login from './components/common/Auth/Login';
import MyTeams from './pages/UBTAdmin/MyTeams/MyTeams';
import History from './pages/UBTAdmin/History/History';
import ContactUs from './pages/UBTAdmin/ContactUs/ContactUs';
import Members from './pages/UBTAdmin/Members/Members';
import AddUBTProfile from './pages/UBTAdmin/Profile/AddUBTProfile';
import FindAndSelectUBT from './pages/UBTAdmin/FindAndSelectUBT/FindAndSelectUBT';
import ManageAccessWithEdit from './pages/ManageAccess/ManageAccessWithEdit';
import ManageAccessPage from './pages/ManageAccess/ManageAccessPage';
import PathPerformance from './pages/PathPerformance/PathPerformance';
import ProjectDetails from './pages/UBTAdmin/ProjectDetails/DetailsContainer';
import ProtectedRoute from './components/common/Auth/ProtectedRoute';
import RoleBasedRoute from './components/common/Auth/RoleBasedRoute';
import EmployeeErrorGuard from './components/common/Auth/EmployeeErrorGuard';

import PageNotFound from './pages/PageNotFound/PageNotFound';
import AnalyticsRoute from './components/common/AnalyticsRoute';
import TeamsReporting from "./pages/UBTAdmin/TeamsReporting/TeamsReporting";
import SearchProject from './pages/UBTAdmin/SearchProject/SearchUBTProjects';
import TeamDocuments from './pages/UBTAdmin/TeamDocuments/TeamDocuments';
import OfflineBanner from './components/common/OfflineBanner';
import MaintenanceBanner from './components/common/MaintenanceBanner';
import TestDataCharts from './pages/UBTAdmin/TestDataCharts/TestDataCharts';

const App: React.FC = () => {
   const [overlayVisible, setOverlayVisible] = useState(false)

  useEffect(() => {
    const onOpen = () => setOverlayVisible(true)
    const onClose = () => setOverlayVisible(false)
    globalThis.addEventListener('app-modal-open', onOpen)
    globalThis.addEventListener('app-modal-close', onClose)
    return () => {
      globalThis.removeEventListener('app-modal-open', onOpen)
      globalThis.removeEventListener('app-modal-close', onClose)
    }
  }, [])
  return (
    <ReduxProvider>
      <Router>
          <DocumentTitle />
            {/* App-level overlay to cover header + body when modal is open */}
            <div
            aria-hidden={!overlayVisible}
            className={`app-overlay ${overlayVisible ? 'app-overlay--visible' : ''}`}
          />
          <Routes>
          {/* Public routes - no protection */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Protected routes - all app routes wrapped with layout */}
          <Route
            path="/*"
            element={     
                <ProtectedRoute>
                  <ProfileProvider>
                    <EmployeeProvider>
                      <div className="app">
                        <Header />
                        <OfflineBanner />
                        <MaintenanceBanner />
                        <main className="flex-1">
                          <Routes>
                            {/* Redirect root to UBT Admin */}
                            <Route path="/" element={<Navigate to="/home" replace />} />
                            <Route path="/home" element={
                              <AnalyticsRoute pageKey="HOME" contentTitle="Home">
                                <UBTLandingPage />
                              </AnalyticsRoute>
                            } />
                            <Route element={<EmployeeErrorGuard />}>
                              <Route path="/home/my-teams" element={
                                <AnalyticsRoute pageKey="MY_TEAMS" contentTitle="My Teams">
                                  <MyTeams />
                                </AnalyticsRoute>
                              } />
                              <Route path="/home/projects" element={
                                <AnalyticsRoute pageKey="PROJECTS" contentTitle="Projects">
                                  <ProjectDetails />
                                </AnalyticsRoute>
                              } />
                              <Route path="/home/history" element={
                                <AnalyticsRoute pageKey="HISTORY" contentTitle="History">
                                  <History />
                                </AnalyticsRoute>
                              } />
                              <Route path="/home/contact-us" element={
                                <AnalyticsRoute pageKey="CONTACT_US" contentTitle="Contact Us">
                                  <ContactUs />
                                </AnalyticsRoute>
                              } />
                              <Route path="/home/select-ubt" element={
                                <AnalyticsRoute pageKey="SELECT_UBT" contentTitle="Select UBT">
                                  <FindAndSelectUBT mode="select" />
                                </AnalyticsRoute>
                              } />
                              <Route path="/home/members" element={
                                <AnalyticsRoute pageKey="MEMBERS" contentTitle="Members">
                                  <Members />
                                </AnalyticsRoute>
                              } />
                              {/* Role-restricted routes: Admin only */}
                              <Route path="/home/add-profile" element={
                                <AnalyticsRoute pageKey="ADD_PROFILE" contentTitle="Add Profile">
                                  <RoleBasedRoute><AddUBTProfile /></RoleBasedRoute>
                                </AnalyticsRoute>
                              } />
                              <Route path="/home/edit-ubt" element={
                                <AnalyticsRoute pageKey="EDIT_UBT" contentTitle="Edit UBT">
                                  <RoleBasedRoute><FindAndSelectUBT mode="edit" /></RoleBasedRoute>
                                </AnalyticsRoute>
                              } />
                              {/* Role-restricted routes: Admin can manage, LMP can view only */}
                              <Route path="/home/manage-access" element={
                                <AnalyticsRoute pageKey="MANAGE_ACCESS" contentTitle="Manage Access">
                                  <RoleBasedRoute><ManageAccessWithEdit /></RoleBasedRoute>
                                </AnalyticsRoute>
                              } />
                              <Route path="/home/manage-access/add-access" element={
                                <AnalyticsRoute pageKey="MANAGE_ACCESS_ADD" contentTitle="Add Access">
                                  <RoleBasedRoute><ManageAccessPage /></RoleBasedRoute>
                                </AnalyticsRoute>
                              }/>
                              <Route path="/home/search-teams" element={
                                <AnalyticsRoute pageKey="SEARCH_PROJECTS" contentTitle="Search Projects">
                                  <SearchProject />
                                </AnalyticsRoute>
                              }/>
                              <Route path="/home/path-to-performance" element={
                                <AnalyticsRoute pageKey="PATH_TO_PERFORMANCE" contentTitle="Path to Performance">
                                  <PathPerformance />
                                </AnalyticsRoute>
                              } />
                              <Route path="/home/teams-reporting" element={
                                <AnalyticsRoute pageKey="TEAMS_REPORTING" contentTitle="Teams Reporting">
                                  <TeamsReporting />
                                </AnalyticsRoute>
                              } />
                              <Route path="/home/team-documents" element={
                                <AnalyticsRoute pageKey="TEAM_DOCUMENTS" contentTitle="Team Documents">
                                  <TeamDocuments />
                                </AnalyticsRoute>
                              } />
                              <Route path="/home/test-data-charts" element={
                                <AnalyticsRoute pageKey="TEST_DATA_CHARTS" contentTitle="Test Data Charts">
                                  <TestDataCharts />
                                </AnalyticsRoute>
                              } />
                              <Route path="*" element={
                                <AnalyticsRoute pageKey="NOT_FOUND" contentTitle="Page Not Found">
                                  <PageNotFound/>
                                </AnalyticsRoute>
                              } />
                            </Route>
                          </Routes>
                        </main>
                        <Footer />
                      </div>
                    </EmployeeProvider>
                  </ProfileProvider>
                </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </ReduxProvider>
  );
};

export default App;